import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { IFilter } from '@epicuro-next/ataraxia/models';
import { IRange } from '@epicuro-next/ataraxia/multi-range-slider';
import { getMaxDisplayValue } from '@epicuro-next/utilities';
import {
  Filter,
  RangeFilterValue,
  RangeItem,
} from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'epc-products-filter-range',
  templateUrl: './products-filter-range.component.html',
  styleUrls: ['./products-filter-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductsFilterRangeComponent implements OnChanges {
  public currentFilter?: Filter;

  public isFilterChanging = false;

  @Input()
  public rangeFilters: Filter[];

  @Input() public activeFilter: IFilter;

  @Input()
  public unit: string;

  @Input()
  public rangeGroupName: string;

  @Input()
  public multiRangeSliderInputs = false;

  @Output()
  public rangeChanged = new EventEmitter<IFilter>();

  public minRange$ = new BehaviorSubject<number>(0);
  public maxRange$ = new BehaviorSubject<number>(1);

  public displayedMax$ = new BehaviorSubject<number | undefined>(undefined);

  public rangeFilterRange: IRange;

  public rangeControl: FormControl<IRange> = new FormControl();

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnChanges(): void {
    if (this.activeFilter === undefined && !this.isFilterChanging) {
      this.resetFilter();
    } else if (this.activeFilter) {
      const range = this.activeFilter.value;

      this.currentFilter = this.rangeFilters.find(
        (x) => x.fieldId === this.activeFilter.fieldId,
      );

      const [min, max] = range.split('-');

      this.setRangeValuesOnFilterChange(this.currentFilter, {
        min: Number(min),
        max: Number(max),
      });
      this.isFilterChanging = false;
    }
  }

  public get radioGroupName(): string {
    return (
      this.rangeGroupName ??
      this.rangeFilters.map((x) => x.fieldId).join('-') ??
      'pricefilters'
    );
  }

  public filterChanged(fieldId: string) {
    this.isFilterChanging = true;
    const oldFilter = this.rangeFilters.find((x) => x.fieldId !== fieldId);
    const newFilter = this.rangeFilters.find((x) => x.fieldId === fieldId);

    // reset old filter
    if (oldFilter) {
      this.rangeChanged.emit({
        fieldId: oldFilter.fieldId,
        value: '',
      });
    }

    // update new filter
    this.currentFilter = newFilter;
    this.setRangeValuesOnFilterChange(newFilter);
  }

  public onRangeChanged(range: IRange) {
    if (!this.currentFilter) return;

    this.rangeChanged.emit({
      fieldId: this.currentFilter.fieldId,
      value: `${range.min}-${range.max}`,
    });
  }

  private resetFilter() {
    if (this.rangeFilters?.length) {
      this.currentFilter = this.rangeFilters[0];
      this.setRangeValuesOnFilterChange(this.currentFilter);
    }
  }

  private setRangeValuesOnFilterChange(
    filter: Filter | undefined,
    customRange?: IRange,
  ) {
    if (filter === undefined) return;

    const range = (
      (filter?.values?.find(
        (x) => x.value === this.unit,
      ) as RangeFilterValue) ?? filter.values?.[0]
    )?.range as RangeItem[];

    if (range && range.length > 1) {
      const min = Number(range[0].value);
      const max = Number(range[1].value);

      this.minRange$.next(min);
      this.maxRange$.next(max);
      this.displayedMax$.next(getMaxDisplayValue(filter));
      this.cdr.detectChanges();

      const rangeValue = !!customRange ? customRange : { min, max };
      this.rangeControl.setValue(rangeValue);
    }
  }
}
